/**
 * Política de privacidade pública (ADR-089). Cada frase descreve o que o sistema faz hoje —
 * conferido no código em 12/10/2026 — e mudou o sistema, muda aqui no mesmo commit: as
 * plataformas (TikTok, Tray) leem esta página na análise do aplicativo, e a LGPD cobra o que ela
 * promete. Inglês ao lado do português porque a análise das plataformas é em inglês.
 */
export interface PolicySection {
  title: string;
  paragraphs?: string[];
  items?: string[];
}

export interface PolicyText {
  title: string;
  updated: string;
  intro: string;
  sections: PolicySection[];
}

export const PRIVACY_CONTACT = 'privacidade@troig.com.br';
const CONTROLLER = 'Temprafesta, CNPJ 62.224.049/0001-85';

export const PRIVACY_POLICY: Record<'pt' | 'en', PolicyText> = {
  pt: {
    title: 'Política de Privacidade',
    updated: 'Atualizada em 12 de outubro de 2026',
    intro:
      'O troig é uma plataforma de gestão de anúncios, precificação e margem real para quem vende em marketplaces. Esta política explica quais dados pessoais tratamos, para quê, com quem compartilhamos e como você exerce seus direitos, conforme a Lei Geral de Proteção de Dados (Lei 13.709/2018).',
    sections: [
      {
        title: '1. Quem é o responsável',
        paragraphs: [
          `O troig é operado por ${CONTROLLER}, controladora dos dados das contas de usuário.`,
          'Quanto aos dados de compradores que chegam das lojas conectadas, o troig atua como operador: trata esses dados em nome da empresa vendedora, que é a controladora, e só para operar as vendas dela.',
          `Encarregado pela proteção de dados: Sergio Andrade — ${PRIVACY_CONTACT}.`,
        ],
      },
      {
        title: '2. Dados que tratamos',
        items: [
          'Conta de usuário: nome, e-mail e senha. A senha é guardada só como resumo criptográfico (Argon2id), nunca em texto; o segredo do segundo fator de autenticação é guardado criptografado.',
          'Segurança: endereço IP e navegador nas sessões, nos dispositivos confiáveis e no registro de auditoria.',
          'Dados da empresa vendedora: anúncios, preços, estoque, pedidos, custos, tarifas e dados fiscais. As chaves de acesso às lojas e ao ERP são guardadas criptografadas (AES-256-GCM).',
          'Dados de compradores: nos canais que informam o destinatário da entrega (como Mercado Livre e Shopee), nome e endereço ficam criptografados e são apagados 90 dias após a entrega. As mensagens pós-venda são apagadas 90 dias após a última mensagem. Não guardamos telefone nem documento (CPF) de compradores.',
          'TikTok Shop: os dados de comprador que vêm nas respostas da plataforma são descartados no processamento e nunca gravados; do pedido ficam apenas produto, quantidade, preço e situação.',
        ],
      },
      {
        title: '3. Para que usamos',
        items: [
          'Prestar o serviço contratado: sincronizar anúncios e pedidos, calcular margem e preço e executar as ações que o usuário pede (base legal: execução de contrato).',
          'Proteger as contas e o sistema: autenticação, prevenção de fraude e auditoria (base legal: legítimo interesse e segurança).',
          'Cumprir obrigações legais e regulatórias.',
        ],
        paragraphs: ['Não vendemos dados pessoais e não os usamos para publicidade.'],
      },
      {
        title: '4. Com quem compartilhamos',
        items: [
          'Plataformas de venda e ERPs conectados pela empresa vendedora, só para executar o que ela pede.',
          'Hospedagem: os servidores do troig ficam no Brasil.',
          'Provedor de envio de e-mail, para mensagens da conta (convite, redefinição de senha).',
          'Provedores de inteligência artificial: recebem dados de anúncios, preços e indicadores para análises e sugestões e, quando o usuário pede uma resposta sugerida, o texto da mensagem do comprador. Alguns desses provedores processam os dados fora do Brasil, como nos Estados Unidos; enviamos só o necessário para a função pedida (LGPD, art. 33).',
          'Autoridades, quando houver obrigação legal ou ordem judicial.',
        ],
      },
      {
        title: '5. Cookies',
        paragraphs: [
          'Usamos apenas cookies essenciais: o da sessão e o de proteção contra falsificação de requisição. Não usamos cookies de análise nem de publicidade.',
        ],
      },
      {
        title: '6. Segurança',
        items: [
          'Tráfego sempre em HTTPS.',
          'Criptografia dos dados confidenciais em repouso (AES-256-GCM) e das senhas (Argon2id).',
          'Segundo fator de autenticação obrigatório para administradores e limite de tentativas de login.',
          'Isolamento entre empresas: cada usuário só acessa os dados da própria empresa.',
          'Registro de auditoria das ações relevantes e cópias de segurança diárias do banco de dados.',
        ],
      },
      {
        title: '7. Por quanto tempo guardamos',
        items: [
          'Conta e dados da empresa: enquanto o contrato estiver ativo.',
          'Sessões e códigos de acesso vencidos: apagados em até 30 dias.',
          'Nome e endereço de destinatário e mensagens pós-venda: 90 dias, como descrito acima.',
          'Ao fim do contrato, os dados da empresa são excluídos em até 30 dias após o pedido, salvo o que a lei obrigar a manter; as cópias de segurança expiram no ciclo normal de rotação.',
        ],
      },
      {
        title: '8. Seus direitos',
        paragraphs: [
          `Você pode pedir confirmação de tratamento, acesso, correção, anonimização ou eliminação, portabilidade, informação sobre compartilhamento e revogação de consentimento (LGPD, art. 18), pelo e-mail ${PRIVACY_CONTACT}. Respondemos em até 15 dias. Você também pode peticionar à Autoridade Nacional de Proteção de Dados (ANPD).`,
          'Se você é comprador de uma loja que usa o troig, fale com a loja; se o pedido chegar a nós, ajudamos a loja a atendê-lo.',
        ],
      },
      {
        title: '9. Incidentes de segurança',
        paragraphs: [
          'Se houver incidente que possa causar risco ou dano relevante aos titulares, comunicamos a ANPD, as empresas afetadas e os titulares, conforme o art. 48 da LGPD.',
        ],
      },
      {
        title: '10. Alterações',
        paragraphs: [
          'Quando esta política mudar, a nova versão é publicada nesta página com a data de atualização.',
        ],
      },
    ],
  },
  en: {
    title: 'Privacy Policy',
    updated: 'Last updated: October 12, 2026',
    intro:
      'troig is a listing management, pricing and real-margin platform for marketplace sellers. This policy explains which personal data we process, why, with whom we share it and how you can exercise your rights under Brazil’s General Data Protection Law (LGPD, Law 13.709/2018).',
    sections: [
      {
        title: '1. Who is responsible',
        paragraphs: [
          `troig is operated by ${CONTROLLER}, the controller of user account data.`,
          'For buyer data received from connected stores, troig acts as a processor: it handles that data on behalf of the selling company, which is the controller, and only to operate that company’s sales.',
          `Data Protection Officer: Sergio Andrade — ${PRIVACY_CONTACT}.`,
        ],
      },
      {
        title: '2. Data we process',
        items: [
          'User account: name, email and password. Passwords are stored only as a cryptographic hash (Argon2id), never in plain text; the two-factor authentication secret is stored encrypted.',
          'Security: IP address and browser in sessions, trusted devices and the audit log.',
          'Seller company data: listings, prices, stock, orders, costs, fees and tax data. Store and ERP access tokens are stored encrypted (AES-256-GCM).',
          'Buyer data: on channels that provide the shipping recipient (such as Mercado Livre and Shopee), name and address are stored encrypted and erased 90 days after delivery. Post-sale messages are erased 90 days after the last message. We do not store buyers’ phone numbers or national ID (CPF).',
          'TikTok Shop: buyer data returned by the platform’s APIs is discarded during processing and never stored; only product, quantity, price and status are kept from each order.',
        ],
      },
      {
        title: '3. Why we use it',
        items: [
          'To provide the contracted service: synchronize listings and orders, calculate margin and price, and carry out the actions the user requests (legal basis: performance of contract).',
          'To protect accounts and the system: authentication, fraud prevention and auditing (legal basis: legitimate interest and security).',
          'To comply with legal and regulatory obligations.',
        ],
        paragraphs: ['We do not sell personal data and do not use it for advertising.'],
      },
      {
        title: '4. Who we share it with',
        items: [
          'Sales platforms and ERPs connected by the selling company, only to carry out its requests.',
          'Hosting: troig servers are located in Brazil.',
          'Email delivery provider, for account messages (invitations, password reset).',
          'Artificial intelligence providers: they receive listing, price and performance data for analyses and suggestions and, when the user asks for a suggested reply, the text of the buyer’s message. Some of these providers process data outside Brazil, such as in the United States; we send only what the requested function needs (LGPD, art. 33).',
          'Authorities, when required by law or court order.',
        ],
      },
      {
        title: '5. Cookies',
        paragraphs: [
          'We use only essential cookies: the session cookie and the request-forgery protection cookie. We do not use analytics or advertising cookies.',
        ],
      },
      {
        title: '6. Security',
        items: [
          'All traffic over HTTPS.',
          'Encryption of confidential data at rest (AES-256-GCM) and of passwords (Argon2id).',
          'Mandatory two-factor authentication for administrators and login rate limiting.',
          'Tenant isolation: each user can access only their own company’s data.',
          'Audit log of relevant actions and daily database backups.',
        ],
      },
      {
        title: '7. How long we keep it',
        items: [
          'Account and company data: while the contract is active.',
          'Expired sessions and access codes: deleted within 30 days.',
          'Recipient name and address and post-sale messages: 90 days, as described above.',
          'At the end of the contract, the company’s data is deleted within 30 days of the request, except what the law requires us to keep; backups expire in the normal rotation cycle.',
        ],
      },
      {
        title: '8. Your rights',
        paragraphs: [
          `You may request confirmation of processing, access, correction, anonymization or deletion, portability, information about sharing and withdrawal of consent (LGPD, art. 18) at ${PRIVACY_CONTACT}. We reply within 15 days. You may also file a complaint with Brazil’s National Data Protection Authority (ANPD).`,
          'If you are a buyer from a store that uses troig, contact the store; if your request reaches us, we help the store fulfill it.',
        ],
      },
      {
        title: '9. Security incidents',
        paragraphs: [
          'If an incident may cause relevant risk or harm to data subjects, we notify the ANPD, the affected companies and the data subjects, as required by LGPD art. 48.',
        ],
      },
      {
        title: '10. Changes',
        paragraphs: [
          'When this policy changes, the new version is published on this page with its update date.',
        ],
      },
    ],
  },
};
